/**
 * Resolve README-relative URLs the same way github.com does: paths are relative
 * to the README file, and images load from raw.githubusercontent.com.
 */

/**
 * @param {string} readmePath e.g. "README.md" or "docs/README.md"
 */
export function getReadmeBaseDir(readmePath) {
  const p = readmePath || "README.md";
  const i = p.lastIndexOf("/");
  return i === -1 ? "" : p.slice(0, i + 1);
}

/**
 * @param {string} url
 * @returns {{ pathPart: string, hash: string }}
 */
function splitHash(url) {
  const hashIdx = url.indexOf("#");
  if (hashIdx === -1) return { pathPart: url, hash: "" };
  return {
    pathPart: url.slice(0, hashIdx),
    hash: url.slice(hashIdx),
  };
}

/**
 * @param {string} owner
 * @param {string} repo
 * @param {string} branch
 * @param {string} readmePath
 * @param {string} url
 * @returns {string}
 */
export function resolveReadmeImageUrl(owner, repo, branch, readmePath, url) {
  const u = (url || "").trim();
  if (!u) return u;
  if (/^data:/i.test(u)) return u;
  if (u.startsWith("#")) return u;

  const { pathPart, hash } = splitHash(u);

  if (/^https?:\/\//i.test(pathPart)) {
    const m = pathPart.match(
      /^https?:\/\/github\.com\/([^/]+)\/([^/]+)\/blob\/([^/]+)\/(.+)$/i
    );
    if (m) {
      const [, o, r, b, rest] = m;
      return `https://raw.githubusercontent.com/${o}/${r}/${b}/${rest}${hash}`;
    }
    return u;
  }

  if (pathPart.startsWith("//")) {
    return `https:${pathPart}${hash}`;
  }

  const baseDir = getReadmeBaseDir(readmePath);
  let pathFromRoot;
  if (pathPart.startsWith("/")) {
    pathFromRoot = pathPart.slice(1);
  } else {
    try {
      const resolved = new URL(pathPart, `https://placeholder.invalid/${baseDir}`);
      pathFromRoot = resolved.pathname.replace(/^\//, "");
    } catch {
      pathFromRoot = `${baseDir}${pathPart}`.replace(/^\.\//, "");
    }
  }

  const segments = pathFromRoot.split("/").filter(Boolean);
  const rawPath = segments.map(encodeURIComponent).join("/");
  return `https://raw.githubusercontent.com/${owner}/${repo}/${branch}/${rawPath}${hash}`;
}

/**
 * Relative links open the file on GitHub (blob), not raw.
 * @param {string} owner
 * @param {string} repo
 * @param {string} branch
 * @param {string} readmePath
 * @param {string} url
 * @returns {string}
 */
export function resolveReadmeLinkUrl(owner, repo, branch, readmePath, url) {
  const u = (url || "").trim();
  if (!u) return u;
  if (u.startsWith("#")) return u;
  if (/^(mailto:|tel:)/i.test(u)) return u;
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith("//")) return `https:${u}`;

  const { pathPart, hash } = splitHash(u);

  const baseDir = getReadmeBaseDir(readmePath);
  let pathFromRoot;
  if (pathPart.startsWith("/")) {
    pathFromRoot = pathPart.slice(1);
  } else {
    try {
      const resolved = new URL(pathPart, `https://placeholder.invalid/${baseDir}`);
      pathFromRoot = resolved.pathname.replace(/^\//, "");
    } catch {
      pathFromRoot = `${baseDir}${pathPart}`.replace(/^\.\//, "");
    }
  }

  const segments = pathFromRoot.split("/").filter(Boolean);
  const blobPath = segments.map(encodeURIComponent).join("/");
  return `https://github.com/${owner}/${repo}/blob/${branch}/${blobPath}${hash}`;
}
